import { useEffect, useState } from 'react';
import {
  Settings, LayoutDashboard, Users, KeyRound, UsersRound, ScrollText,
  FileBarChart, RefreshCw, Cloud, CheckCircle2, AlertTriangle, Wallet, Search,
  ShieldCheck, Copy, X,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import CostManagementPanel from '../components/CostManagementPanel';

const TABS = [
  { key: 'dashboard', label: 'Dashboard',     Icon: LayoutDashboard },
  { key: 'users',     label: 'IAM Users',     Icon: Users },
  { key: 'roles',     label: 'IAM Roles',     Icon: KeyRound },
  { key: 'groups',    label: 'IAM Groups',    Icon: UsersRound },
  { key: 'policies',  label: 'Policies',      Icon: ScrollText },
  { key: 'reports',   label: 'Reports',       Icon: FileBarChart },
  { key: 'cost',      label: 'Cost Management', Icon: Wallet },
  { key: 'settings',  label: 'AWS Settings',  Icon: Settings },
];

export default function AwsGovernance() {
  const [tab, setTab] = useState('dashboard');
  const [bump, setBump] = useState(0);
  const [detail, setDetail] = useState(null);          // { kind: 'user'|'role'|'group'|'policy', data: {...} }
  const [userFilter, setUserFilter] = useState({});    // { noMfa, risk, query }
  const refresh = () => setBump((b) => b + 1);

  // Drill-down from a KPI tile on Dashboard → switch tab + apply filter
  const drillFromKpi = (key) => {
    if (key === 'users' || key === 'nomfa' || key === 'risk') {
      setUserFilter({
        noMfa: key === 'nomfa',
        risk: key === 'risk' ? 'high' : '',
        query: '',
      });
      setTab('users');
    } else if (key === 'roles') setTab('roles');
    else if (key === 'groups') setTab('groups');
    else if (key === 'policies') setTab('policies');
    else if (key === 'stale') {
      // Stale keys → users list sorted by max_key_age (no dedicated filter, jump there)
      setUserFilter({ noMfa: false, risk: '', query: '' });
      setTab('users');
      toast.info('Sort by Max Key Age to find stale keys.');
    } else if (key === 'sync') setTab('settings');
  };

  return (
    <div className="space-y-5" data-testid="aws-governance-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">
          <a href="/security" className="hover:text-[#3D4A8E]" data-testid="aws-breadcrumb-security">Security &amp; Governance</a> · AWS
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1 inline-flex items-center gap-2">
          <Cloud className="w-7 h-7 text-amber-600" /> AWS Governance
        </h1>
        <p className="text-slate-500 mt-1">Read-only AWS IAM visibility · MFA &amp; key hygiene · Cost Explorer. Click any KPI tile or row for details.</p>
      </div>

      <div className="flex flex-wrap gap-1 border-b-2 border-slate-200 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`aws-tab-${t.key}`}
            className={`px-4 py-2 text-sm font-semibold inline-flex items-center gap-1.5 border-b-2 -mb-0.5 transition-colors whitespace-nowrap ${
              tab === t.key ? 'border-[#3D4A8E] text-[#3D4A8E]' : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
          >
            <t.Icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && <DashboardTab bump={bump} onDrill={drillFromKpi} />}
      {tab === 'users'     && <UsersTab onOpen={(u) => setDetail({ kind: 'user',  data: u })} initialFilter={userFilter} />}
      {tab === 'roles'     && <RolesTab onOpen={(r) => setDetail({ kind: 'role',  data: r })} />}
      {tab === 'groups'    && <GroupsTab onOpen={(g) => setDetail({ kind: 'group', data: g })} />}
      {tab === 'policies'  && <PoliciesTab onOpen={(p) => setDetail({ kind: 'policy', data: p })} />}
      {tab === 'reports'   && <ReportsTab />}
      {tab === 'cost'      && <CostManagementPanel provider="aws" />}
      {tab === 'settings'  && <SettingsTab onSynced={refresh} />}

      {detail && <AwsDetailDrawer detail={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
function DashboardTab({ bump, onDrill }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get('/aws/dashboard'); setD(data); }
      catch { setD({ configured: false, message: 'Failed to load' }); }
    })();
  }, [bump]);
  if (d === null) return <div className="text-sm text-slate-500">Loading…</div>;
  if (!d?.configured) return <EmptyState message={d?.message || 'AWS credentials not configured'} />;

  const cards = [
    { key: 'users',   label: 'IAM Users',      value: d.users_total,    accent: 'text-amber-600 bg-amber-50' },
    { key: 'roles',   label: 'IAM Roles',      value: d.roles_total,    accent: 'text-orange-600 bg-orange-50' },
    { key: 'groups',  label: 'IAM Groups',     value: d.groups_total,   accent: 'text-emerald-600 bg-emerald-50' },
    { key: 'policies',label: 'Policies',       value: d.policies_total, accent: 'text-blue-600 bg-blue-50' },
    { key: 'nomfa',   label: 'No-MFA Users',   value: d.users_no_mfa,   accent: 'text-rose-600 bg-rose-50' },
    { key: 'risk',    label: 'High-risk Users',value: d.users_high_risk,accent: 'text-rose-600 bg-rose-50' },
    { key: 'stale',   label: 'Stale Keys (1yr+)',value: d.stale_keys,   accent: 'text-fuchsia-600 bg-fuchsia-50' },
    { key: 'sync',    label: 'Last sync',      value: d.last_sync_at ? new Date(d.last_sync_at).toLocaleDateString() : 'never', accent: 'text-slate-600 bg-slate-100' },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="aws-kpi-strip">
      {cards.map((c) => (
        <button key={c.key} type="button"
          onClick={() => onDrill?.(c.key)}
          className="bg-white border border-slate-200 rounded-2xl p-4 flex items-start justify-between hover:border-[#3D4A8E] hover:shadow-md transition-all text-left group"
          data-testid={`aws-kpi-${c.key}`}>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 group-hover:text-[#3D4A8E]">{c.label}</div>
            <div className="font-heading text-xl font-bold text-slate-900 mt-1">{c.value ?? '—'}</div>
            <div className="text-[10px] text-slate-400 mt-0.5 group-hover:text-[#3D4A8E]">Click to drill down →</div>
          </div>
          <div className={`w-9 h-9 rounded-xl ${c.accent} flex items-center justify-center`}>
            <Cloud className="w-4 h-4" strokeWidth={1.8} />
          </div>
        </button>
      ))}
    </div>
  );
}

function UsersTab({ onOpen, initialFilter }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState(initialFilter?.query || '');
  const [risk, setRisk] = useState(initialFilter?.risk || '');
  const [noMfa, setNoMfa] = useState(!!initialFilter?.noMfa);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      const p = new URLSearchParams();
      if (q) p.set('q', q);
      if (risk) p.set('risk', risk);
      if (noMfa) p.set('no_mfa', 'true');
      try { const { data } = await api.get(`/aws/users?${p}`); if (!cancelled) setRows(data || []); }
      catch { if (!cancelled) setRows([]); }
    }, 200);
    return () => { clearTimeout(t); cancelled = true; };
  }, [q, risk, noMfa]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <div className="relative max-w-sm flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name / ARN" className="rounded-xl pl-9" data-testid="aws-user-search" />
        </div>
        <select value={risk} onChange={(e) => setRisk(e.target.value)} className="rounded-xl border border-slate-200 px-3 text-sm" data-testid="aws-user-risk">
          <option value="">All risks</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
        </select>
        <label className="inline-flex items-center gap-2 text-sm text-slate-600 px-3">
          <input type="checkbox" checked={noMfa} onChange={(e) => setNoMfa(e.target.checked)} data-testid="aws-user-nomfa" />
          No MFA only
        </label>
        <div className="ml-auto text-xs text-slate-500 self-center"><b>{rows.length}</b> user(s)</div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">MFA</th>
              <th className="text-left px-4 py-3">Max Key Age</th>
              <th className="text-left px-4 py-3">Last Sign-in</th>
              <th className="text-left px-4 py-3">Risk</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id}
                  onClick={() => onOpen?.(u)}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  data-testid={`aws-user-${u.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-900">{u.name}</div>
                  <div className="text-[10px] text-slate-500 font-mono truncate max-w-md" title={u.arn}>{u.arn}</div>
                </td>
                <td className="px-4 py-3">{u.mfa_enabled ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-rose-600" />}</td>
                <td className="px-4 py-3 text-xs">{u.max_key_age_days != null ? `${u.max_key_age_days} days` : '—'}</td>
                <td className="px-4 py-3 text-xs">{u.password_last_used ? new Date(u.password_last_used).toLocaleDateString() : 'never'}</td>
                <td className="px-4 py-3"><RiskBadge level={u.risk_level} /></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 py-10 text-sm">No users. Sync from AWS Settings.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RolesTab({ onOpen }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  useEffect(() => {
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/aws/roles${q ? `?q=${encodeURIComponent(q)}` : ''}`); setRows(data || []); }
      catch { setRows([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="space-y-3">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search roles…" className="rounded-xl max-w-sm" data-testid="aws-role-search" />
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
            <tr><th className="text-left px-4 py-3">Role</th><th className="text-left px-4 py-3">Created</th><th className="text-left px-4 py-3">Description</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} onClick={() => onOpen?.(r)}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  data-testid={`aws-role-${r.id}`}>
                <td className="px-4 py-3"><div className="font-semibold">{r.name}</div><div className="text-[10px] text-slate-500 font-mono">{r.arn}</div></td>
                <td className="px-4 py-3 text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{r.description || '—'}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={3} className="text-center text-slate-400 py-10 text-sm">No roles.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GroupsTab({ onOpen }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { (async () => { try { const { data } = await api.get('/aws/groups'); setRows(data || []); } catch { setRows([]); } })(); }, []);
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
          <tr><th className="text-left px-4 py-3">Group</th><th className="text-left px-4 py-3">Members</th><th className="text-left px-4 py-3">Created</th></tr>
        </thead>
        <tbody>
          {rows.map((g) => (
            <tr key={g.id} onClick={() => onOpen?.(g)}
                className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                data-testid={`aws-group-${g.id}`}>
              <td className="px-4 py-3 font-semibold">{g.name}</td>
              <td className="px-4 py-3 text-xs">{g.member_count} member(s)</td>
              <td className="px-4 py-3 text-xs">{g.created_at ? new Date(g.created_at).toLocaleDateString() : '—'}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={3} className="text-center text-slate-400 py-10 text-sm">No groups.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function PoliciesTab({ onOpen }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { (async () => { try { const { data } = await api.get('/aws/policies'); setRows(data || []); } catch { setRows([]); } })(); }, []);
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
          <tr><th className="text-left px-4 py-3">Policy</th><th className="text-left px-4 py-3">Attachments</th><th className="text-left px-4 py-3">Updated</th></tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id} onClick={() => onOpen?.(p)}
                className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                data-testid={`aws-policy-${p.id}`}>
              <td className="px-4 py-3"><div className="font-semibold">{p.name}</div><div className="text-[10px] text-slate-500 font-mono">{p.arn}</div></td>
              <td className="px-4 py-3 text-xs">{p.attachment_count || 0}</td>
              <td className="px-4 py-3 text-xs">{p.updated_at ? new Date(p.updated_at).toLocaleDateString() : '—'}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={3} className="text-center text-slate-400 py-10 text-sm">No policies.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function ReportsTab() {
  const [reports, setReports] = useState([]);
  useEffect(() => { (async () => { try { const { data } = await api.get('/aws/reports/list'); setReports(data || []); } catch { setReports([]); } })(); }, []);
  const token = localStorage.getItem('hrms_token');
  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-500">Download AWS IAM &amp; cost reports for compliance evidence.</div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {reports.map((r) => (
          <div key={r.kind} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`aws-report-${r.kind}`}>
            <div className="font-semibold text-slate-900 text-sm">{r.label}</div>
            <div className="text-[11px] text-slate-500 font-mono mt-0.5">{r.kind}</div>
            <div className="flex gap-1.5 mt-3 flex-wrap">
              <a href={`/api/aws/reports/${r.kind}?format=pdf&auth=${token}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100" data-testid={`aws-report-pdf-${r.kind}`}>PDF</a>
              <a href={`/api/aws/reports/${r.kind}?format=xlsx&auth=${token}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100" data-testid={`aws-report-xlsx-${r.kind}`}>Excel</a>
              <a href={`/api/aws/reports/${r.kind}?format=csv&auth=${token}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-slate-50 text-slate-700 border border-slate-200 hover:bg-slate-100" data-testid={`aws-report-csv-${r.kind}`}>CSV</a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsTab({ onSynced }) {
  const [form, setForm] = useState({
    access_key_id: '', secret_access_key: '', region: 'us-east-1',
    organization_enabled: false, budget_monthly_usd: '', cost_cache_enabled: true,
  });
  const [info, setInfo] = useState({ secret_set: false, last_sync_at: null, last_sync_status: null });
  const [busy, setBusy] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/aws/settings');
        setInfo(data);
        setForm((f) => ({ ...f,
          access_key_id: data.access_key_id || '',
          region: data.region || 'us-east-1',
          organization_enabled: !!data.organization_enabled,
          budget_monthly_usd: data.budget_monthly_usd || '',
          cost_cache_enabled: data.cost_cache_enabled !== false,
        }));
      } catch { /* ignore */ }
    })();
  }, []);

  const save = async () => {
    setBusy('save');
    try {
      const payload = {
        access_key_id: form.access_key_id,
        region: form.region,
        organization_enabled: form.organization_enabled,
        budget_monthly_usd: form.budget_monthly_usd ? Number(form.budget_monthly_usd) : 0,
        cost_cache_enabled: form.cost_cache_enabled,
      };
      if (form.secret_access_key) payload.secret_access_key = form.secret_access_key;
      await api.put('/aws/settings', payload);
      toast.success('AWS settings saved');
      setForm((f) => ({ ...f, secret_access_key: '' }));
      setInfo((i) => ({ ...i, secret_set: form.secret_access_key ? true : i.secret_set }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(''); }
  };

  const testConn = async () => {
    setBusy('test');
    try {
      const { data } = await api.post('/aws/test-connection');
      if (data.ok) toast.success(`Connected. Account ${data.account_id}`);
      else toast.error(data.detail || 'Test failed');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Test failed'); }
    finally { setBusy(''); }
  };

  const sync = async () => {
    setBusy('sync');
    try {
      const { data } = await api.post('/aws/sync');
      const s = data.summary || {};
      toast.success(`Synced: ${s.users || 0} users · ${s.roles || 0} roles · ${s.groups || 0} groups · ${s.policies || 0} policies`);
      onSynced?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Sync failed'); }
    finally { setBusy(''); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 max-w-2xl space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
        <div className="font-semibold mb-1">Required IAM permissions on this Access Key:</div>
        <ul className="list-disc list-inside space-y-0.5">
          <li><code>ReadOnlyAccess</code> (AWS-managed) — IAM read</li>
          <li><code>AWSBillingReadOnlyAccess</code> — Cost Explorer</li>
          <li><code>AWSOrganizationsReadOnlyAccess</code> — multi-account (optional)</li>
        </ul>
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-600">AWS Access Key ID *</label>
        <Input value={form.access_key_id} onChange={(e) => setForm({ ...form, access_key_id: e.target.value })}
               className="rounded-xl mt-1 font-mono" placeholder="AKIA…" data-testid="aws-access-key-id" />
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-600">
          AWS Secret Access Key {info.secret_set && <span className="text-emerald-700 font-normal">· already stored (leave blank to keep)</span>}
        </label>
        <Input type="password" value={form.secret_access_key}
               onChange={(e) => setForm({ ...form, secret_access_key: e.target.value })}
               className="rounded-xl mt-1 font-mono" placeholder={info.secret_set ? '••••••••••••••' : 'Paste secret'} data-testid="aws-secret-access-key" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-slate-600">Default region</label>
          <Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}
                 className="rounded-xl mt-1 font-mono" placeholder="us-east-1" data-testid="aws-region" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600">Monthly budget (USD)</label>
          <Input type="number" min={0} step={100} value={form.budget_monthly_usd || ''}
                 onChange={(e) => setForm({ ...form, budget_monthly_usd: e.target.value })}
                 placeholder="e.g., 5000" className="rounded-xl mt-1" data-testid="aws-budget" />
        </div>
      </div>
      <label className="inline-flex items-center gap-2 text-sm text-slate-600">
        <input type="checkbox" checked={form.organization_enabled}
               onChange={(e) => setForm({ ...form, organization_enabled: e.target.checked })}
               data-testid="aws-org-enabled" />
        Enable AWS Organizations (multi-account)
      </label>

      {/* Cost Explorer cache toggle — $0.01/call savings */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 space-y-2" data-testid="aws-cost-cache-card">
        <label className="inline-flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
          <input type="checkbox" checked={form.cost_cache_enabled}
                 onChange={(e) => setForm({ ...form, cost_cache_enabled: e.target.checked })}
                 className="mt-1"
                 data-testid="aws-cost-cache-toggle" />
          <span>
            <b>Auto-cache Cost Explorer data (10-min TTL)</b>
            <div className="text-[11px] text-slate-600 mt-0.5">
              AWS Cost Explorer charges <b>$0.01 per API call</b>. When enabled, dashboard responses are cached server-side for 10 minutes so repeated page loads don&apos;t hit your bill.
              When disabled, every page load fetches fresh data — useful when you&apos;re actively budget-tuning and want second-by-second accuracy.
              You can always hit <b>&quot;Refresh now&quot;</b> on the Cost dashboard to force-fetch regardless of this setting.
            </div>
          </span>
        </label>
        <div className="text-[10px] text-indigo-700 pl-6">
          Current state: <b>{form.cost_cache_enabled ? 'CACHED (10-min TTL)' : 'LIVE (every request hits AWS)'}</b>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap pt-2 border-t border-slate-100">
        <Button onClick={save} disabled={busy === 'save' || !form.access_key_id}
                className="rounded-full bg-[#3D4A8E] hover:bg-[#2F3A6E]" data-testid="aws-save-btn">
          {busy === 'save' ? 'Saving…' : 'Save settings'}
        </Button>
        <Button onClick={testConn} disabled={busy === 'test'} variant="outline" className="rounded-full" data-testid="aws-test-btn">
          {busy === 'test' ? 'Testing…' : 'Test connection'}
        </Button>
        <Button onClick={sync} disabled={busy === 'sync'} variant="outline" className="rounded-full" data-testid="aws-sync-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${busy === 'sync' ? 'animate-spin' : ''}`} />
          {busy === 'sync' ? 'Syncing…' : 'Sync IAM now'}
        </Button>
      </div>
      <div className="text-xs text-slate-500 border-t border-slate-100 pt-3">
        Last sync: <b>{info.last_sync_at ? new Date(info.last_sync_at).toLocaleString() : 'never'}</b>
        {info.last_sync_status && <> · status <b>{info.last_sync_status}</b></>}
      </div>

      <AwsGrantAccessWizard />
    </div>
  );
}

// --------------------------------------------------------------------------
// "Grant Access" wizard — generates a one-shot AWS CLI / PowerShell script
// that recreates the read-only IAM user + 4 managed policies. Mirrors the
// Azure wizard in CostManagementPanel.jsx.
// --------------------------------------------------------------------------
function AwsGrantAccessWizard() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('cli');
  const [userName, setUserName] = useState('lanvaus-hrms-readonly');

  const cliScript = `# Lanvaus HRMS — AWS read-only IAM provisioning
# Run in AWS CloudShell or any shell with AWS CLI authenticated as an admin.

USER_NAME="${userName}"

# 1) Create the IAM user
aws iam create-user --user-name "$USER_NAME"

# 2) Attach the 4 managed policies (read-only across IAM, Billing, Org, Audit)
for POLICY in \\
  "arn:aws:iam::aws:policy/ReadOnlyAccess" \\
  "arn:aws:iam::aws:policy/AWSBillingReadOnlyAccess" \\
  "arn:aws:iam::aws:policy/AWSOrganizationsReadOnlyAccess" \\
  "arn:aws:iam::aws:policy/SecurityAudit"; do
  aws iam attach-user-policy --user-name "$USER_NAME" --policy-arn "$POLICY"
done

# 3) Create a programmatic access key and print credentials
echo "Creating access key — copy these into HRMS → AWS Settings:"
aws iam create-access-key --user-name "$USER_NAME" \\
  --query 'AccessKey.{AccessKeyId:AccessKeyId,SecretAccessKey:SecretAccessKey}' \\
  --output table

# 4) Print user ARN for reference
aws iam get-user --user-name "$USER_NAME" --query 'User.Arn' --output text

echo ""
echo "Done. Paste the AccessKeyId + SecretAccessKey into HRMS → Security → AWS → AWS Settings."`;

  const psScript = `# Lanvaus HRMS — AWS read-only IAM provisioning (PowerShell)
# Requires AWS Tools for PowerShell. Authenticate as an admin first via Set-AWSCredentials.

$UserName = "${userName}"

# 1) Create the IAM user
New-IAMUser -UserName $UserName

# 2) Attach the 4 managed policies
$Policies = @(
  "arn:aws:iam::aws:policy/ReadOnlyAccess",
  "arn:aws:iam::aws:policy/AWSBillingReadOnlyAccess",
  "arn:aws:iam::aws:policy/AWSOrganizationsReadOnlyAccess",
  "arn:aws:iam::aws:policy/SecurityAudit"
)
foreach ($Arn in $Policies) {
  Register-IAMUserPolicy -UserName $UserName -PolicyArn $Arn
}

# 3) Create a programmatic access key — STORE THE SECRET, it's only shown once
$Key = New-IAMAccessKey -UserName $UserName
Write-Host "AccessKeyId    : $($Key.AccessKeyId)"
Write-Host "SecretAccessKey: $($Key.SecretAccessKey)"

# 4) Print user ARN
Write-Host "User ARN: $((Get-IAMUser -UserName $UserName).Arn)"

Write-Host ""
Write-Host "Paste the AccessKeyId + SecretAccessKey into HRMS -> Security -> AWS -> AWS Settings."`;

  const script = tab === 'cli' ? cliScript : psScript;
  const cloudShellUrl = 'https://console.aws.amazon.com/cloudshell/home';

  const copy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(script);
        toast.success('Copied. Paste into AWS CloudShell.');
        return;
      }
    } catch { /* fall through */ }
    try {
      const ta = document.createElement('textarea');
      ta.value = script;
      ta.style.position = 'fixed'; ta.style.left = '-9999px';
      ta.setAttribute('readonly', '');
      document.body.appendChild(ta);
      ta.select(); ta.setSelectionRange(0, script.length);
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) { toast.success('Copied. Paste into AWS CloudShell.'); return; }
    } catch { /* both methods failed */ }
    toast.error('Browser blocked auto-copy. Click the script and use Ctrl+A → Ctrl+C.');
  };

  return (
    <div className="border-t border-slate-100 pt-4 mt-2" data-testid="aws-grant-access-wizard">
      <button onClick={() => setOpen(!open)}
        className="w-full text-left flex items-center justify-between gap-2 hover:opacity-80"
        data-testid="aws-grant-access-toggle">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#3D4A8E] inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> Grant Access Wizard
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            One-shot AWS CLI / PowerShell script that re-creates the read-only IAM user + 4 managed policies (ReadOnlyAccess, Billing, Organizations, SecurityAudit).
          </div>
        </div>
        <span className="text-xs text-slate-400">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="mt-4 space-y-3">
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="text-[11px] font-semibold text-slate-600">IAM user name to create</label>
              <Input value={userName} onChange={(e) => setUserName(e.target.value.trim() || 'lanvaus-hrms-readonly')}
                     className="rounded-xl mt-1 font-mono w-72"
                     data-testid="aws-grant-username" />
            </div>
            <div className="flex gap-1 border border-slate-200 rounded-full p-1 bg-slate-50 w-fit ml-auto" data-testid="aws-grant-tabs">
              <button onClick={() => setTab('cli')}
                className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${tab === 'cli' ? 'bg-[#3D4A8E] text-white' : 'text-slate-600'}`}
                data-testid="aws-grant-tab-cli">AWS CLI (bash)</button>
              <button onClick={() => setTab('ps')}
                className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${tab === 'ps' ? 'bg-[#3D4A8E] text-white' : 'text-slate-600'}`}
                data-testid="aws-grant-tab-ps">PowerShell</button>
            </div>
          </div>

          <div className="relative">
            <pre className="bg-slate-900 text-slate-100 text-[11px] font-mono p-4 rounded-2xl overflow-x-auto leading-relaxed cursor-text select-all"
                 data-testid="aws-grant-script"
                 onClick={(e) => {
                   const range = document.createRange();
                   range.selectNodeContents(e.currentTarget);
                   const sel = window.getSelection();
                   sel.removeAllRanges(); sel.addRange(range);
                 }}>{script}</pre>
            <Button onClick={copy} size="sm"
              className="absolute top-2 right-2 rounded-full bg-slate-700 hover:bg-slate-600 text-white h-7 px-3 text-[11px]"
              data-testid="aws-grant-copy">
              <Copy className="w-3 h-3 mr-1" /> Copy
            </Button>
          </div>

          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-xs text-indigo-900">
            <div className="font-semibold mb-1">How to use:</div>
            <ol className="list-decimal list-inside space-y-0.5 text-[11px]">
              <li>Click <b>Copy</b> above.</li>
              <li>Open <a href={cloudShellUrl} target="_blank" rel="noreferrer" className="underline font-semibold" data-testid="aws-grant-cloud-shell-link">AWS CloudShell</a> ({tab === 'cli' ? 'bash' : 'install PowerShell module first via <code>Install-Module AWSPowerShell.NetCore</code>'}).</li>
              <li>Paste the script and press Enter. It will create the user, attach 4 policies, and print the new <code>AccessKeyId</code> + <code>SecretAccessKey</code>.</li>
              <li>Copy those two values into the form above, click <b>Save settings</b>, then <b>Test connection</b> and <b>Sync IAM now</b>.</li>
            </ol>
            <div className="mt-2 text-[10px] text-indigo-700">
              <b>Note:</b> The shell user running these commands needs <code>iam:CreateUser</code>, <code>iam:AttachUserPolicy</code>, and <code>iam:CreateAccessKey</code> — i.e., an IAM admin. The newly-created user itself has read-only scope only.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RiskBadge({ level }) {
  const styles = {
    high: 'bg-rose-50 text-rose-700 border-rose-200',
    medium: 'bg-amber-50 text-amber-700 border-amber-200',
    low: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  };
  return <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${styles[level] || styles.low}`}>{level || 'low'}</span>;
}

function EmptyState({ message }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
      <Cloud className="w-10 h-10 text-slate-300 mx-auto" strokeWidth={1.5} />
      <div className="text-slate-700 font-semibold mt-3">{message}</div>
      <div className="text-xs text-slate-500 mt-1">Open the <b>AWS Settings</b> tab to configure the Access Key + Secret.</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AWS Detail Drawer — opens when a row/tile is clicked. Renders all relevant
// fields for the selected IAM User / Role / Group / Policy.
// ---------------------------------------------------------------------------
function AwsDetailDrawer({ detail, onClose }) {
  const { kind, data } = detail;
  const titleByKind = { user: 'IAM User', role: 'IAM Role', group: 'IAM Group', policy: 'Policy' };
  const eyebrow = titleByKind[kind] || 'AWS Resource';

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="aws-detail-drawer">
      <div className="flex-1 bg-slate-900/40" onClick={onClose} />
      <div className="w-full max-w-xl bg-white border-l border-slate-200 overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-start justify-between gap-2 z-10">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">{eyebrow}</div>
            <div className="font-heading text-lg font-bold text-slate-900 mt-0.5 truncate">{data.name || data.id}</div>
            {data.arn && <div className="text-[11px] text-slate-500 font-mono break-all mt-0.5">{data.arn}</div>}
            {data.risk_level && <div className="mt-2"><RiskBadge level={data.risk_level} /></div>}
          </div>
          <button onClick={onClose} className="rounded-full p-1 hover:bg-slate-100" data-testid="aws-drawer-close">
            <X className="w-4 h-4 text-slate-600" />
          </button>
        </div>

        <div className="p-4 space-y-5">
          {kind === 'user' && <AwsUserDetail u={data} />}
          {kind === 'role' && <AwsRoleDetail r={data} />}
          {kind === 'group' && <AwsGroupDetail g={data} />}
          {kind === 'policy' && <AwsPolicyDetail p={data} />}
        </div>
      </div>
    </div>
  );
}

function KvRow({ k, v, mono }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-slate-100 last:border-0 text-sm">
      <span className="text-slate-500 flex-shrink-0">{k}</span>
      <span className={`text-slate-900 font-medium text-right break-all ${mono ? 'font-mono text-[11px]' : ''}`}>
        {v == null || v === '' ? '—' : v}
      </span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">{title}</div>
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3">{children}</div>
    </div>
  );
}

function AwsUserDetail({ u }) {
  const keys = u.access_keys || [];
  return (
    <>
      <Section title="Identity">
        <KvRow k="User name" v={u.name} />
        <KvRow k="ARN" v={u.arn} mono />
        <KvRow k="User ID" v={u.user_id} mono />
        <KvRow k="Path" v={u.path || '/'} mono />
        <KvRow k="Created" v={u.created_at ? new Date(u.created_at).toLocaleString() : '—'} />
      </Section>

      <Section title="Security Posture">
        <KvRow k="MFA enabled" v={u.mfa_enabled ? <span className="text-emerald-700 font-bold">Yes</span> : <span className="text-rose-700 font-bold">No</span>} />
        <KvRow k="Risk level" v={<RiskBadge level={u.risk_level} />} />
        <KvRow k="Has console password" v={u.has_console_password ? 'Yes' : 'No'} />
        <KvRow k="Password last used" v={u.password_last_used ? new Date(u.password_last_used).toLocaleString() : 'never'} />
        <KvRow k="Max access-key age" v={u.max_key_age_days != null ? `${u.max_key_age_days} days` : '—'} />
      </Section>

      <Section title={`Access Keys (${keys.length})`}>
        {keys.length === 0 && <div className="text-xs text-slate-400">No programmatic access keys.</div>}
        {keys.map((k, i) => (
          <div key={k.id || i} className="border-t border-slate-200 first:border-0 pt-2 first:pt-0 mt-2 first:mt-0">
            <div className="text-[11px] font-mono text-slate-700">{k.id}</div>
            <div className="flex gap-3 text-[11px] mt-0.5">
              <span>Status: <b className={k.status === 'Active' ? 'text-emerald-700' : 'text-slate-500'}>{k.status}</b></span>
              <span>Age: <b className={k.age_days > 90 ? 'text-rose-700' : 'text-slate-700'}>{k.age_days ?? '—'} days</b></span>
              <span>Created: {k.created_at ? new Date(k.created_at).toLocaleDateString() : '—'}</span>
            </div>
          </div>
        ))}
      </Section>

      {Array.isArray(u.groups) && u.groups.length > 0 && (
        <Section title="Group Membership">
          <div className="flex flex-wrap gap-1.5">
            {u.groups.map((g) => (
              <span key={g} className="px-2 py-0.5 bg-white border border-slate-200 rounded-full text-[11px] text-slate-700">{g}</span>
            ))}
          </div>
        </Section>
      )}

      {Array.isArray(u.attached_policies) && u.attached_policies.length > 0 && (
        <Section title="Attached Managed Policies">
          <div className="space-y-1">
            {u.attached_policies.map((p, i) => (
              <div key={i} className="text-[11px] text-slate-700">{typeof p === 'string' ? p : (p.name || p.arn)}</div>
            ))}
          </div>
        </Section>
      )}

      {Array.isArray(u.risk_reasons) && u.risk_reasons.length > 0 && (
        <Section title="Why this risk level?">
          <ul className="list-disc list-inside text-xs text-rose-700 space-y-0.5">
            {u.risk_reasons.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </Section>
      )}
    </>
  );
}

function AwsRoleDetail({ r }) {
  return (
    <>
      <Section title="Identity">
        <KvRow k="Role name" v={r.name} />
        <KvRow k="ARN" v={r.arn} mono />
        <KvRow k="Path" v={r.path || '/'} mono />
        <KvRow k="Created" v={r.created_at ? new Date(r.created_at).toLocaleString() : '—'} />
        <KvRow k="Max session duration" v={r.max_session_duration ? `${r.max_session_duration}s` : '—'} />
      </Section>
      <Section title="Description">
        <div className="text-sm text-slate-700">{r.description || 'No description set.'}</div>
      </Section>
      {r.assume_role_policy && (
        <Section title="Trust Policy (assume role)">
          <pre className="text-[10px] font-mono text-slate-800 whitespace-pre-wrap bg-white p-2 rounded-lg border border-slate-200 max-h-80 overflow-y-auto">
            {typeof r.assume_role_policy === 'string' ? r.assume_role_policy : JSON.stringify(r.assume_role_policy, null, 2)}
          </pre>
        </Section>
      )}
      {Array.isArray(r.attached_policies) && r.attached_policies.length > 0 && (
        <Section title="Attached Managed Policies">
          <div className="space-y-1">
            {r.attached_policies.map((p, i) => (
              <div key={i} className="text-[11px] text-slate-700">{typeof p === 'string' ? p : (p.name || p.arn)}</div>
            ))}
          </div>
        </Section>
      )}
    </>
  );
}

function AwsGroupDetail({ g }) {
  return (
    <>
      <Section title="Identity">
        <KvRow k="Group name" v={g.name} />
        <KvRow k="ARN" v={g.arn} mono />
        <KvRow k="Path" v={g.path || '/'} mono />
        <KvRow k="Created" v={g.created_at ? new Date(g.created_at).toLocaleString() : '—'} />
        <KvRow k="Member count" v={g.member_count ?? 0} />
      </Section>
      {Array.isArray(g.members) && g.members.length > 0 && (
        <Section title={`Members (${g.members.length})`}>
          <div className="flex flex-wrap gap-1.5">
            {g.members.map((m) => (
              <span key={m} className="px-2 py-0.5 bg-white border border-slate-200 rounded-full text-[11px] text-slate-700">{m}</span>
            ))}
          </div>
        </Section>
      )}
      {Array.isArray(g.attached_policies) && g.attached_policies.length > 0 && (
        <Section title="Attached Managed Policies">
          <div className="space-y-1">
            {g.attached_policies.map((p, i) => (
              <div key={i} className="text-[11px] text-slate-700">{typeof p === 'string' ? p : (p.name || p.arn)}</div>
            ))}
          </div>
        </Section>
      )}
    </>
  );
}

function AwsPolicyDetail({ p }) {
  return (
    <>
      <Section title="Identity">
        <KvRow k="Policy name" v={p.name} />
        <KvRow k="ARN" v={p.arn} mono />
        <KvRow k="Policy ID" v={p.policy_id} mono />
        <KvRow k="Path" v={p.path || '/'} mono />
        <KvRow k="Default version" v={p.default_version_id} />
        <KvRow k="Created" v={p.created_at ? new Date(p.created_at).toLocaleString() : '—'} />
        <KvRow k="Updated" v={p.updated_at ? new Date(p.updated_at).toLocaleString() : '—'} />
      </Section>
      <Section title="Attachments">
        <KvRow k="Attached to" v={`${p.attachment_count ?? 0} principal(s)`} />
      </Section>
      {p.description && (
        <Section title="Description">
          <div className="text-sm text-slate-700 whitespace-pre-wrap">{p.description}</div>
        </Section>
      )}
      {p.policy_document && (
        <Section title="Policy Document">
          <pre className="text-[10px] font-mono text-slate-800 whitespace-pre-wrap bg-white p-2 rounded-lg border border-slate-200 max-h-80 overflow-y-auto">
            {typeof p.policy_document === 'string' ? p.policy_document : JSON.stringify(p.policy_document, null, 2)}
          </pre>
        </Section>
      )}
    </>
  );
}
